import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    FaUsers,
    FaGraduationCap,
    FaBriefcase,
    FaMoneyBillWave,
    FaArrowUp,
    FaArrowDown,
    FaCalendarAlt
} from 'react-icons/fa';
import WaveChart from '../shared/WaveChart';
import DataTable from '../shared/DataTable';
import Loader from '../../shared/Loader';
import './DashboardOverview.css';

const DashboardOverview = () => {
    const [stats, setStats] = useState({
        totalUsers: 0,
        activeCourses: 0,
        pendingJobs: 0,
        monthlyRevenue: 0
    });
    const [activityData, setActivityData] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

                // Fetch stats and recent activity simultaneously
                const [statsRes, activityRes] = await Promise.all([
                    axios.get('/api/admin/dashboard-stats', { headers }),
                    axios.get('/api/admin/audit-recent', { headers })
                ]);

                if (statsRes.data) {
                    setStats(prevStats => ({
                        ...prevStats,
                        totalUsers: statsRes.data.totalUsers || 0,
                        activeCourses: statsRes.data.activeCourses || 0,
                        pendingJobs: statsRes.data.pendingJobs || 0,
                        monthlyRevenue: statsRes.data.monthlyRevenue || 0
                    }));
                }

                setActivityData(activityRes.data);

                setTimeout(() => {
                    setLoading(false);
                }, 500);
            } catch (err) {
                console.error("Error fetching dashboard data", err);
                setTimeout(() => {
                    setLoading(false);
                }, 500);
            }
        };
        fetchData();
    }, []);

    // Mock Data for the Wave Charts
    const revenueData = [
        { name: 'Jan', revenue: 45000 },
        { name: 'Feb', revenue: 52000 },
        { name: 'Mar', revenue: 48000 },
        { name: 'Apr', revenue: 61000 },
        { name: 'May', revenue: 55000 },
        { name: 'Jun', revenue: 67000 },
        { name: 'Jul', revenue: 85400 },
    ];

    const growthData = [
        { name: 'Mon', users: 120 },
        { name: 'Tue', users: 210 },
        { name: 'Wed', users: 150 },
        { name: 'Thu', users: 280 },
        { name: 'Fri', users: 240 },
        { name: 'Sat', users: 180 },
        { name: 'Sun', users: 310 },
    ];


    const activityColumns = [
        { header: 'Action', key: 'action', width: '20%' },
        { header: 'User', key: 'user', width: '25%' },
        { header: 'Details', key: 'details', width: '25%' },
        {
            header: 'Status', key: 'status', width: '15%', render: (row) => (
                <span className={`status-badge ${row.status}`}>
                    {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
                </span>
            )
        },
        { header: 'Time', key: 'timestamp', width: '15%', render: (row) => new Date(row.timestamp).toLocaleTimeString() },
    ];

    if (loading) {
        return <Loader />;
    }

    return (
        <div className="dashboard-overview fade-in">
            <div className="dashboard-header">
                <h1 className="page-title">Morning, Super Admin</h1>
                <p className="page-subtitle">Here's a look at what's happening across the platform today.</p>
            </div>

            {/* KPI Cards */}
            <div className="kpi-grid">
                <div className="kpi-card" onClick={() => navigate('/admin/users')} style={{ cursor: 'pointer' }}>
                    <div className="kpi-header">
                        <span className="kpi-title">Total Users</span>
                        <div className="kpi-icon-wrapper"><FaUsers /></div>
                    </div>
                    <h2 className="kpi-value">{stats.totalUsers.toLocaleString()}</h2>
                    <div className="kpi-trend">
                        <span style={{ color: '#94A3B8', fontWeight: 400 }}>Platform-wide count</span>
                    </div>
                </div>

                <div className="kpi-card" onClick={() => navigate('/admin/courses')} style={{ cursor: 'pointer' }}>
                    <div className="kpi-header">
                        <span className="kpi-title">Active Courses</span>
                        <div className="kpi-icon-wrapper"><FaGraduationCap /></div>
                    </div>
                    <h2 className="kpi-value">{stats.activeCourses.toLocaleString()}</h2>
                    <div className="kpi-trend">
                        <span style={{ color: '#94A3B8', fontWeight: 400 }}>Live on platform</span>
                    </div>
                </div>

                <div className="kpi-card" onClick={() => navigate('/admin/jobs')} style={{ cursor: 'pointer' }}>
                    <div className="kpi-header">
                        <span className="kpi-title">Pending Jobs</span>
                        <div className="kpi-icon-wrapper"><FaBriefcase /></div>
                    </div>
                    <h2 className="kpi-value">{stats.pendingJobs.toLocaleString()}</h2>
                    <div className="kpi-trend">
                        <span style={{ color: '#94A3B8', fontWeight: 400 }}>awaiting approval</span>
                    </div>
                </div>

                <div className="kpi-card" onClick={() => navigate('/admin/payments')} style={{ cursor: 'pointer' }}>
                    <div className="kpi-header">
                        <span className="kpi-title">Monthly Revenue</span>
                        <div className="kpi-icon-wrapper"><FaMoneyBillWave /></div>
                    </div>
                    <h2 className="kpi-value">${stats.monthlyRevenue.toLocaleString()}</h2>
                    <div className="kpi-trend">
                        <span style={{ color: '#94A3B8', fontWeight: 400 }}>Current month earnings</span>
                    </div>
                </div>
            </div>

            {/* Charts Section */}
            <div className="charts-grid">
                <div className="chart-card">
                    <div className="section-title">
                        <span>Revenue Performance</span>
                        <div style={{ fontSize: '12px', color: '#64748B', fontWeight: 500 }}>
                            <FaCalendarAlt style={{ marginRight: '6px' }} />
                            Last 7 Months
                        </div>
                    </div>
                    <WaveChart data={revenueData} xKey="name" yKey="revenue" color="#002C5F" />
                </div>
                <div className="chart-card">
                    <div className="section-title">
                        <span>Daily User Growth</span>
                    </div>
                    <WaveChart data={growthData} xKey="name" yKey="users" color="#D4AF37" height={300} />
                </div>
            </div>

            {/* Recent Activity Table using our New shared component */}
            <DataTable
                title="Recent Platform Activity"
                columns={activityColumns}
                data={activityData}
                actions={
                    <button className="primary-btn sm pulse-on-hover" onClick={() => navigate('/admin/audit')}>
                        View All Activity
                    </button>
                }
                filterOptions={[
                    { label: 'Completed', value: 'completed' },
                    { label: 'Warning', value: 'warning' },
                    { label: 'Failed', value: 'failed' }
                ]}
            />
        </div>
    );
};

export default DashboardOverview;
